import {
  IAddOpportunityFormModel,
  DensityType,
} from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useFormikContext, useField } from 'formik'
import { useCallback, useMemo, useEffect } from 'react'
import {
  ISalesFunnel,
  useSelectFunnelStagesByType,
} from '@user/store/salesFunnel'

import { useSelector } from 'react-redux'
import { selectAllSalesFunnels } from '@user/store/salesFunnel/slice'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { ENTITY_TYPES } from '@sales-center/common'

type Props = {
  name: string
}

export function useData(props: Props) {
  const { name } = props
  const tidalThemeActive = useTidalThemeActive()

  const {
    setFieldValue,
    isSubmitting,
    values: { salesFunnelId, opportunityStageId, leadId },
  } = useFormikContext<IAddOpportunityFormModel>()

  const [{}, { error, touched }] = useField<string[]>({
    name,
    validate: (value) => {
      if (!value) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const unsortedSalesFunnels = useSelector(selectAllSalesFunnels)

  const salesFunnelStages = useSelectFunnelStagesByType(
    +salesFunnelId,
    ENTITY_TYPES.Opportunity,
  )

  const options = useMemo(
    () =>
      unsortedSalesFunnels.sort((a, b) => {
        if (a.isDefault) return -1
        else if (b.isDefault) return 1
        return a.name.localeCompare(b.name)
      }),
    [unsortedSalesFunnels],
  )

  const getOptionLabel = useCallback(
    (option: ISalesFunnel) =>
      option.isDefault ? `${option?.name} (Default)` : option?.name,
    [],
  )

  const getId = useCallback((option: ISalesFunnel) => {
    return String(option.id)
  }, [])

  const initialOption = useMemo(() => {
    if (salesFunnelId) {
      const value = unsortedSalesFunnels.find((x) => x.id == salesFunnelId)

      if (value) return value
    }
    if (!salesFunnelId) {
      const value = unsortedSalesFunnels.find((x) => x.isDefault)
      if (value) return value
    }
    return null
  }, [unsortedSalesFunnels, salesFunnelId])

  useEffect(() => {
    if (!salesFunnelId) {
      const value = options.find((x) => x.isDefault)
      if (value) {
        setFieldValue('salesFunnelId', value.id)
        setFieldValue('opportunityStageId', '')
      }
    }
    if (
      leadId &&
      salesFunnelId &&
      !opportunityStageId &&
      salesFunnelStages?.length > 0
    ) {
      setFieldValue('opportunityStageId', salesFunnelStages[0].id)
    }
  }, [salesFunnelId, salesFunnelStages])

  const density: DensityType = tidalThemeActive ? 'comfortable' : 'compact'

  return {
    getOptionLabel,
    getId,
    options,
    initialOption,
    isSubmitting,
    showError,
    helperText,
    density,
  }
}
